import { useParams } from 'react-router-dom';
import { UUID } from '../../../../../../../types/shared/UUID';
import { useGetCloser } from './useGetCloser';
import { useCallback, useMemo } from 'react';
import { emdAccessCodeValidation } from '../emdAccessCodeValidation';
import {
  AccessCodeCreateRequest,
  usePostAccessLinkMutation,
} from '../../../../../../../API/order/payments/EmdAccessCodeApi';
import { checkForMutationSuccess } from '../../../../../../../API/checkForMutationSuccess';
import { eDepositorType } from '../../../../../../../types/order/emdAccess/eDepositorType';
import { useGetSetOfPreviousDepositorIds } from './useGetSetOfPreviousDepositorIds';

const getInitialValues = (orderId: UUID): AccessCodeCreateRequest => ({
  orderId,
  depositorId: '',
  depositorType: eDepositorType.Person,
});

export const useSetRecipient = (onClose: () => void) => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const { assignedCloser } = useGetCloser();
  const initialValues = useMemo(() => getInitialValues(orderId), [orderId]);
  const setOfPreviousDepositorIds = useGetSetOfPreviousDepositorIds();
  const [postAccessLink, { isLoading }] = usePostAccessLinkMutation();

  const onSubmit = useCallback(
    async (values: AccessCodeCreateRequest) => {
      const action = getAction(values, setOfPreviousDepositorIds);
      const generateLinkResult = await postAccessLink(values);
      if (checkForMutationSuccess(generateLinkResult, action)) {
        onClose();
      }
    },
    [onClose, postAccessLink, setOfPreviousDepositorIds]
  );

  return {
    assignedCloser,
    initialValues,
    isLoading,
    onSubmit,
    validationSchema: emdAccessCodeValidation,
  };
};

const getAction = (values: AccessCodeCreateRequest, setOfPreviousDepositorIds: Set<UUID>) => {
  if (setOfPreviousDepositorIds.has(values.depositorId)) {
    return 'regenerating Access Code for Acorn. The previous Access Code is now disabled.';
  }
  return 'generating Access Code for Acorn';
};
